import { useTranslation } from 'react-i18next';
import cardTexture from '../../assets/figma/card-texture.jpg';
import { MastercardLogo, SnowflakeIcon, VisaLogo } from './icons';

type Props = {
  balance?: string;
  /** Last 4 digits of the PAN — shown bottom-right for card identification. */
  last4?: string;
  /** User label or product name, shown under the INCPT wordmark. */
  label?: string | null;
  /** Show Mastercard logo instead of Visa (templates 14/24/27). */
  mastercard?: boolean;
  /** Illustration variant: only the INCPT wordmark + network logo, no balance row. */
  minimal?: boolean;
  /** Desaturate + frost overlay — matches CardLarge frozen look. */
  frozen?: boolean;
  /** Stretch to the container width instead of the fixed preview size — used
   *  by the vertical cards list, where every row spans the screen. */
  full?: boolean;
  className?: string;
};

/**
 * Holographic INCPT card: iridescent texture + inner shadow, wordmark, balance and last4.
 */
export function HolographicCard({
  balance,
  last4,
  label,
  mastercard,
  minimal,
  frozen,
  full,
  className,
}: Props) {
  const { t } = useTranslation();
  const cardName = label?.trim() || null;
  return (
    <div
      className={`relative overflow-hidden rounded-[20px] text-left ${
        full ? 'aspect-[275/174] w-full' : 'h-[174px] w-[275px] shrink-0'
      } ${frozen ? 'saturate-[0.5]' : ''} ${className ?? ''}`}
    >
      <img
        src={cardTexture}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[20px] shadow-[inset_0px_0px_40px_0px_rgba(0,0,0,0.16)]"
      />

      <div className="relative flex h-full flex-col justify-between p-5">
        <div className="flex items-start justify-between gap-3">
          {/* items-start + text-left: <button> defaults to text-align:center and
              was centering INCPT / label inside the left column. */}
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
            <span className="text-[20px] font-semibold leading-none text-ink">INCPT</span>
            {cardName && (
              <span className="max-w-full truncate text-[12px] font-medium leading-none text-ink/55">
                {cardName}
              </span>
            )}
          </div>
          {minimal ? null : mastercard ? (
            <MastercardLogo className="h-[27px] w-[44px] shrink-0 object-contain" />
          ) : (
            <VisaLogo className="h-[27px] w-[81px] shrink-0 object-contain" />
          )}
        </div>
        {minimal ? (
          <div className="flex justify-end">
            {mastercard ? (
              <MastercardLogo className="h-[22px] w-[36px] object-contain" />
            ) : (
              <VisaLogo className="h-[22px] w-[66px] object-contain" />
            )}
          </div>
        ) : (
          <div className="flex items-end justify-between">
            <span className="text-[22px] font-medium leading-none tabular-nums text-ink">
              {balance}
            </span>
            {last4 ? (
              <span className="text-[14px] leading-none tracking-[0.08em] tabular-nums text-black/[0.46]">
                •••• {last4}
              </span>
            ) : null}
          </div>
        )}
      </div>

      <div
        aria-hidden={!frozen}
        className={`pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 rounded-[20px] bg-white/40 backdrop-blur-[3px] transition-opacity duration-500 ${
          frozen ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <SnowflakeIcon
          size={32}
          className="text-white drop-shadow-[0px_2px_6px_rgba(0,0,0,0.25)]"
        />
        <span className="text-[12px] font-semibold uppercase tracking-wide text-white drop-shadow-[0px_1px_4px_rgba(0,0,0,0.3)]">
          {t('cards.frozen')}
        </span>
      </div>
    </div>
  );
}
